import React, { useEffect, useState } from 'react';
import api, { documentsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Upload, Download, FileText } from 'lucide-react';

const DOC_CATEGORIES = [
  ['passport', 'Passport'], ['visa', 'Visa'], ['work_permit', 'Work Permit'],
  ['emirates_id', 'Emirates ID'], ['national_id', 'National ID'],
  ['educational_certificate', 'Educational Certificate'], ['experience_certificate', 'Experience Certificate'],
  ['medical_insurance', 'Medical Insurance'], ['bank_proof', 'Bank Proof'], ['other', 'Other'],
];

const MyDocuments = () => {
  const [empId, setEmpId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('passport');
  const [expiry, setExpiry] = useState('');
  const [docNumber, setDocNumber] = useState('');

  const fetchAll = async () => {
    try {
      const emp = await api.get('/me/employee');
      setEmpId(emp.data?.id);
      const r = await api.get('/me/documents');
      setDocs(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !empId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      if (expiry) fd.append('expiry_date', expiry);
      if (docNumber) fd.append('document_number', docNumber);
      await documentsAPI.upload(empId, fd);
      toast.success('Document uploaded');
      setExpiry(''); setDocNumber('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const download = async (d) => {
    try {
      const res = await documentsAPI.download(d.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = d.file_name || 'document';
      link.click();
    } catch { toast.error('Download failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="my-documents-page">
      <div>
        <h1 className="text-2xl font-bold">My Documents</h1>
        <p className="text-muted-foreground">Upload and manage your personal documents</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Upload New Document</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="my-doc-category"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Document Number</Label><Input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} placeholder="Optional" /></div>
          <div><Label className="text-xs">Expiry (optional)</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} /></div>
          <div className="flex items-end">
            <label className="w-full">
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={upload} data-testid="my-doc-file" />
              <Button asChild disabled={uploading} className="w-full">
                <span>{uploading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}Upload</span>
              </Button>
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">My Documents ({docs.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {docs.length === 0 && <p className="text-muted-foreground text-center py-6">No documents uploaded yet</p>}
          {docs.map(d => (
            <div key={d.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`my-doc-${d.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <FileText size={20} className="text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{d.file_name}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <Badge variant="outline" className="text-xs">{d.category}</Badge>
                    <Badge variant={d.status === 'verified' ? 'default' : d.status === 'rejected' ? 'destructive' : 'secondary'} className="text-xs">{d.status}</Badge>
                    {d.expiry_date && <span className="text-xs text-muted-foreground">Expires: {d.expiry_date}</span>}
                  </div>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => download(d)}><Download size={14} /></Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyDocuments;
